import { motion } from 'motion/react';
import { memo } from 'react';
import { TileCarousel, TileCarouselItem } from '@/components/TileCarousel';
import { TileContent, TileTitle } from '@/components/ui/tile';
import { PLACEHOLDER_IMG_URL } from '@/constants/images';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn, getFirstString } from '@/lib/utils';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponseHit } from '@music-vine/setlist';
interface SearchCarouselProps {
  assetType?: AssetType;
  closePopup: () => void;
  data: SearchResponseHit<GenericSearchResult>[] | undefined;
  onTileClick?: (slug: string) => void;
}
export const SearchCarousel = memo(({
  data,
  closePopup,
  onTileClick,
  assetType
}: SearchCarouselProps) => {
  const {
    isMd
  } = useTWScreens();
  const onClick = (slug: string) => {
    closePopup();
    if (onTileClick) {
      onTileClick(slug);
    }
  };
  if (!data?.length) {
    return null;
  }

  // Use motion.div only on desktop, regular div on mobile to avoid iOS Safari touch issues
  const AnimatedContainer = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} aria-label={`${assetType ?? 'content'} search results carousel`} className={cn('-mx-4 overflow-hidden md:-mx-6',
  // Disable all animations on mobile to fix iOS Safari touch issues
  'max-md:[&_*]:!transition-none max-md:[&_*]:!animation-none')} data-testid={`${assetType ?? 'default'}-search-carousel`} initial={isMd ? {
    opacity: 0
  } : undefined} transition={isMd ? {
    duration: 0.2
  } : undefined}>
        <TileCarousel className="[&_.carousel-content]:mr-3 [&_.carousel-content]:ml-4 md:[&_.carousel-content]:ml-6" variant="globalSearch">
          {data.map((item, index) => {
        if (!(item.document.name && item.document.slug)) {
          return null;
        }
        const tileImage = getFirstString(item.document.image, PLACEHOLDER_IMG_URL);
        const href = item.document.slug;
        return <TileCarouselItem alt={item.document.name}
        // Mobile: ~2 tiles + a bit (48%), Desktop: ~3 tiles + a bit (33%)
        className="!basis-[48%] md:!basis-1/3 pr-2"
        // Tiles here are too small for the hover background to read well
        hover={false} href={href} index={index} key={`${item.document.id}-${href}`} onClick={() => onClick(href)} src={tileImage}>
                <TileContent mode="title-only" visible>
                  <TileTitle className="mt-1 text-sm 2xl:mt-2 2xl:text-base">
                    {item.document.name}
                  </TileTitle>
                </TileContent>
              </TileCarouselItem>;
      })}
        </TileCarousel>
      </AnimatedContainer>;
});
SearchCarousel.displayName = 'SearchCarousel';